import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { PaperclipIcon } from "lucide-react"

import { QuestionPanel } from "@f2e/ui/components/Chat"
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { useTranslation } from "react-i18next"
import {
  useReplyAttachmentCapabilitiesApi,
  useUploadReplyAttachmentApi,
} from "@/hooks/api/histories"
import {
  getUserFacingApiErrorMessage,
  normalizeApiError,
} from "@/lib/apiErrors"
import type { AutosizeTextAreaRef } from "@f2e/ui/components/base-plus"

import { aiDraftTargetKey, type AIDraftTarget } from "@/hooks/api"
import type { AIDraftToolSupportStatus, AIDraftUnsupportedTool } from "@/types"

import {
  AIDraftComposer,
  type AIDraftContextAnchor,
  type AppliedDraftComposerState,
} from "./AIDraft"
import { AIDraftFormPrefillEditor } from "./AIDraftFormPrefillEditor"

export interface AIDraftPlatformReplyComposerProps {
  draftTarget: AIDraftTarget
  message: string
  currentContextAnchor: AIDraftContextAnchor | null
  appliedDraftContext: AppliedDraftComposerState | null
  isSubmitting: boolean
  unsupportedTools?: AIDraftUnsupportedTool[]
  toolSupportStatus?: AIDraftToolSupportStatus
  textareaRef: RefObject<AutosizeTextAreaRef | null>
  placeholder: string
  sendLabel: string
  submittingLabel: string
  notice?: ReactNode
  onMessageChange: (value: string) => void
  onAppliedDraftChange: (draft: AppliedDraftComposerState | null) => void
  onRecordsChanged: () => void
  onBillingChanged: () => void
  onPermissionDenied: () => void
  historyId?: number | null
  channelType?: string | null
  onAttachmentChange?: () => void
  onSend: (attachmentId?: string, onAccepted?: () => void) => void
}

export const AIDraftPlatformReplyComposer = ({
  draftTarget,
  message,
  currentContextAnchor,
  appliedDraftContext,
  isSubmitting,
  unsupportedTools = [],
  toolSupportStatus = "unavailable",
  textareaRef,
  placeholder,
  sendLabel,
  submittingLabel,
  notice,
  onMessageChange,
  onAppliedDraftChange,
  onRecordsChanged,
  onBillingChanged,
  onPermissionDenied,
  onSend,
  historyId,
  channelType,
  onAttachmentChange,
}: AIDraftPlatformReplyComposerProps) => {
  const { t } = useTranslation()
  const { data: capabilities, fetchFn: loadCapabilities } =
    useReplyAttachmentCapabilitiesApi()
  const { fetchFn: upload } = useUploadReplyAttachmentApi()
  const [file, setFile] = useState<File | null>(null)
  const [attachmentId, setAttachmentId] = useState<string>()
  const [uploading, setUploading] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const generation = useRef(0)
  const scope = `${aiDraftTargetKey(draftTarget)}:${historyId}:${channelType}`
  const currentScope = useRef(scope)
  currentScope.current = scope
  const attachmentChangeRef = useRef(onAttachmentChange)
  attachmentChangeRef.current = onAttachmentChange
  const allowed = capabilities?.data?.channels[channelType ?? ""] ?? {}
  const extensions = Object.keys(allowed).flatMap(
    (mime) => capabilities?.data?.extensions[mime] ?? []
  )
  const accept = [...Object.keys(allowed), ...extensions]
  useEffect(() => {
    void loadCapabilities().catch(() => {})
  }, [loadCapabilities])
  useEffect(() => {
    generation.current++
    setFile(null)
    setAttachmentId(undefined)
    setUploading(false)
    setAttachmentError(undefined)
    attachmentChangeRef.current?.()
  }, [scope])
  const removeAttachment = () => {
    generation.current++
    setFile(null)
    setAttachmentId(undefined)
    setUploading(false)
    setAttachmentError(undefined)
    onAttachmentChange?.()
  }
  const uploadFile = async (selected: File) => {
    const version = ++generation.current
    const uploadScope = scope
    setFile(selected)
    setAttachmentId(undefined)
    onAttachmentChange?.()
    setAttachmentError(undefined)
    if (
      !extensions.includes(
        `.${selected.name.split(".").pop()?.toLowerCase()}`
      ) ||
      selected.size === 0 ||
      selected.size > 20_000_000
    ) {
      setAttachmentError(t("history.replyAttachment.invalid"))
      return
    }
    setUploading(true)
    const body = new FormData()
    body.append("data", JSON.stringify({ history_id: historyId }))
    body.append("file", selected)
    try {
      const response = await upload({ body })
      if (
        version !== generation.current ||
        uploadScope !== currentScope.current
      )
        return
      if (!response?.data?.id) throw new Error()
      setAttachmentId(response.data.id)
    } catch (error) {
      const normalized = normalizeApiError(error)
      const invalidFile =
        !normalized.messageKey &&
        typeof normalized.data === "object" &&
        normalized.data !== null &&
        "code" in normalized.data &&
        normalized.data.code === "invalid_reply_attachment"
      if (
        version === generation.current &&
        uploadScope === currentScope.current
      )
        setAttachmentError(
          invalidFile
            ? t("history.replyAttachment.invalid")
            : getUserFacingApiErrorMessage(
                error,
                t,
                t("history.replyAttachment.uploadFailed")
              )
        )
    } finally {
      if (
        version === generation.current &&
        uploadScope === currentScope.current
      )
        setUploading(false)
    }
  }
  const targetKey = aiDraftTargetKey(draftTarget)
  const [pendingTargetKey, setPendingTargetKey] = useState<string | null>(null)

  useEffect(() => {
    setPendingTargetKey(null)
  }, [targetKey])

  const handleAIDraftPendingChange = useCallback(
    (changedTargetKey: string, pending: boolean) => {
      setPendingTargetKey((currentTargetKey) => {
        if (pending) return changedTargetKey
        return currentTargetKey === changedTargetKey ? null : currentTargetKey
      })
    },
    []
  )

  const isAIDraftPending = pendingTargetKey === targetKey
  const formState = appliedDraftContext?.form ?? null
  const handleSend = () => {
    if (
      isAIDraftPending ||
      isSubmitting ||
      uploading ||
      (file && !attachmentId)
    )
      return
    const version = generation.current
    const sendScope = scope
    onSend(attachmentId, () => {
      if (version === generation.current && sendScope === currentScope.current)
        removeAttachment()
    })
  }

  return (
    <div
      aria-busy={isAIDraftPending || isSubmitting}
      data-testid="ai-draft-platform-reply-composer"
    >
      <AIDraftComposer
        key={targetKey}
        draftTarget={draftTarget}
        message={message}
        currentContextAnchor={currentContextAnchor}
        appliedDraftContext={appliedDraftContext}
        disabled={isSubmitting}
        unsupportedTools={unsupportedTools}
        toolSupportStatus={toolSupportStatus}
        textareaRef={textareaRef}
        onMessageChange={onMessageChange}
        onAppliedDraftChange={onAppliedDraftChange}
        onComposerPendingChange={handleAIDraftPendingChange}
        onRecordsChanged={onRecordsChanged}
        onBillingChanged={onBillingChanged}
        onPermissionDenied={onPermissionDenied}
      />
      {appliedDraftContext && formState && (
        <div className="mb-3">
          <AIDraftFormPrefillEditor
            proposal={formState.proposal}
            values={formState.values}
            disabled={isAIDraftPending || isSubmitting}
            onChange={(values) => {
              if (!appliedDraftContext.form) return
              onAppliedDraftChange({
                ...appliedDraftContext,
                form: { ...appliedDraftContext.form, values },
              })
            }}
            onRemove={() =>
              onAppliedDraftChange({ ...appliedDraftContext, form: null })
            }
          />
        </div>
      )}
      {notice}
      {file && (
        <div className="mb-2 text-sm" aria-live="polite">
          {file.name} ·{" "}
          {uploading
            ? t("history.replyAttachment.uploading")
            : t("history.replyAttachment.draft")}
          <Button
            size="sm"
            variant="ghost"
            disabled={isSubmitting}
            onClick={removeAttachment}
          >
            {t("history.replyAttachment.remove")}
          </Button>
          {attachmentError && (
            <>
              <div role="alert" className="text-destructive">
                {attachmentError}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={uploading || isSubmitting}
                onClick={() => void uploadFile(file)}
              >
                {t("history.replyAttachment.retry")}
              </Button>
            </>
          )}
        </div>
      )}
      <QuestionPanel
        textareaRef={textareaRef}
        className="border border-input bg-muted/30 px-3 py-2 text-sm shadow-none focus-visible:border-ring focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:ring-offset-0"
        message={message}
        isAsking={false}
        isSubmitting={isSubmitting}
        disabled={
          isAIDraftPending || uploading || Boolean(file && !attachmentId)
        }
        leftSlot={
          historyId != null && accept.length > 0 ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={t("history.replyAttachment.select")}
                    disabled={isSubmitting || uploading || isAIDraftPending}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <PaperclipIcon className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-72">
                  <div className="font-medium">
                    {t("history.replyAttachment.select")}
                  </div>
                  <p className="mt-1">{t("history.replyAttachment.hint")}</p>
                </TooltipContent>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                aria-label={t("history.replyAttachment.select")}
                accept={accept.join(",")}
                disabled={isSubmitting || uploading || isAIDraftPending}
                onChange={(event) => {
                  const selected = event.target.files?.[0]
                  if (selected) void uploadFile(selected)
                  event.target.value = ""
                }}
              />
            </>
          ) : undefined
        }
        placeholder={placeholder}
        sendLabel={sendLabel}
        submittingLabel={submittingLabel}
        allowAttachmentSend={Boolean(attachmentId)}
        fileBadges={
          file && attachmentId
            ? [
                {
                  id: attachmentId,
                  uuid: attachmentId,
                  name: file.name,
                  type: file.type,
                  status: "uploaded",
                },
              ]
            : []
        }
        onFileRemove={isSubmitting ? undefined : removeAttachment}
        minHeight={88}
        onMessageChange={onMessageChange}
        onSend={handleSend}
      />
    </div>
  )
}
